import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Image as ImageIcon } from 'lucide-react';
import {
  Sparkles, Coffee, GlassWater, Shirt, Cookie, Footprints, Backpack,
  Droplets, Heart, Gem, Gift, Star, Crown, Wand2, Cake, Music, Camera,
  Smile, Flower2, Palette,
} from 'lucide-react';
import { toast } from 'sonner';

const ICON_OPTIONS = [
  'Sparkles', 'Gift', 'Heart', 'Star', 'Crown', 'Wand2', 'Coffee', 'GlassWater',
  'Shirt', 'Cookie', 'Footprints', 'Backpack', 'Droplets', 'Gem', 'Cake', 'Music',
  'Camera', 'Smile', 'Flower2', 'Palette',
];

const ICON_MAP = {
  Sparkles, Gift, Heart, Star, Crown, Wand2, Coffee, GlassWater, Shirt, Cookie,
  Footprints, Backpack, Droplets, Gem, Cake, Music, Camera, Smile, Flower2, Palette,
};

const EMPTY = { name: '', slug: '', icon: 'Gift', image: '' };

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

const slugify = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingSlug, setEditingSlug] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([api.get('/categories'), api.get('/products/admin')]);
    setItems(c.data);
    const counts = {};
    p.data.forEach((prod) => { counts[prod.category] = (counts[prod.category] || 0) + 1; });
    setProductsByCategory(counts);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditingSlug(null); setDialog(true); };
  const openEdit = (c) => { setForm({ name: c.name, slug: c.slug, icon: c.icon || 'Gift', image: c.image || '' }); setEditingSlug(c.slug); setDialog(true); };

  const onName = (v) => {
    setForm((f) => ({
      ...f, name: v,
      // Auto-generate slug only if creating (not editing) or slug was empty
      slug: !editingSlug && (!f.slug || f.slug === slugify(f.name)) ? slugify(v) : f.slug,
    }));
  };

  const onImage = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Imagen demasiado grande (máx 2MB)'); return; }
    const url = await fileToDataUrl(file);
    setForm((f) => ({ ...f, image: url }));
  };

  const save = async () => {
    if (!form.name || !form.slug) return;
    setSaving(true);
    try {
      if (editingSlug) await api.put(`/categories/${editingSlug}`, form);
      else await api.post('/categories', form);
      toast.success(editingSlug ? 'Categoría actualizada' : 'Categoría creada');
      setDialog(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`¿Eliminar la categoría "${slug}"?`)) return;
    try {
      await api.delete(`/categories/${slug}`);
      toast.success('Categoría eliminada');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-slate-900">Categorías</h1>
          <p className="text-slate-500 text-sm">{items.length} categorías en el catálogo</p>
        </div>
        <Button onClick={openNew} data-testid="add-category-btn" className="btn-pink rounded-full">
          <Plus className="h-4 w-4 mr-1" /> Nueva categoría
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => {
          const Icon = ICON_MAP[c.icon] || Gift;
          const count = productsByCategory[c.slug] || 0;
          return (
            <div key={c.slug} data-testid={`category-row-${c.slug}`} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-pinky-50 flex items-center justify-center text-pinky-600 flex-shrink-0">
                <Icon className="h-7 w-7" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg text-slate-900 truncate">{c.name}</p>
                <p className="text-xs text-slate-500 font-mono">/{c.slug}</p>
                <p className="text-xs text-slate-600 mt-1">{count} {count === 1 ? 'producto' : 'productos'}</p>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => openEdit(c)} className="h-8 w-8 rounded-lg hover:bg-pinky-50 text-pinky-600 inline-flex items-center justify-center" data-testid={`edit-cat-${c.slug}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(c.slug)} className="h-8 w-8 rounded-lg hover:bg-rose-50 text-rose-600 inline-flex items-center justify-center" data-testid={`delete-cat-${c.slug}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editingSlug ? 'Editar categoría' : 'Nueva categoría'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={(e) => onName(e.target.value)} placeholder="Ej. Joyería" className="rounded-xl" data-testid="cat-name-input" />
            </div>
            <div>
              <Label>Identificador (slug) *</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} placeholder="joyeria" className="rounded-xl font-mono" data-testid="cat-slug-input" />
              <p className="text-xs text-slate-500 mt-1">Usado en filtros (sin espacios ni acentos). Si lo cambias, los productos se actualizan automáticamente.</p>
            </div>
            <div>
              <Label>Ícono</Label>
              <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                <SelectTrigger className="rounded-xl" data-testid="cat-icon-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {ICON_OPTIONS.map((name) => {
                    const Ico = ICON_MAP[name];
                    return (
                      <SelectItem key={name} value={name}>
                        <span className="inline-flex items-center gap-2"><Ico className="h-4 w-4" /> {name}</span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Imagen (opcional)</Label>
              <div className="mt-1 flex items-center gap-3">
                {form.image ? (
                  <img src={form.image} alt="" className="h-20 w-20 rounded-xl object-cover border border-slate-200" />
                ) : (
                  <div className="h-20 w-20 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                    <ImageIcon className="h-7 w-7" />
                  </div>
                )}
                <input type="file" accept="image/*" onChange={(e) => onImage(e.target.files?.[0])} className="text-sm" data-testid="cat-image-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.slug} className="btn-pink rounded-full" data-testid="cat-save-btn">
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
